import { consola } from 'consola'
import { convertUtcToMoscowTime } from '@shared/lib'
import type { Order, OrderType, PaymentMethod } from '@entities/order'

/**
 * Константы для ключей localStorage
 * 
 * ⚠️ ВАЖНО: UNPAID_ORDER и UNPAID_ORDERS больше не используются!
 * Неоплаченные заказы теперь управляются через polling и Pinia Store.
 */
export const STORAGE_KEYS = {
  UNPAID_ORDER: 'unpaidOrder',      // ❌ DEPRECATED: Один неоплаченный заказ
  UNPAID_ORDERS: 'unpaidOrders',    // ❌ DEPRECATED: Массив неоплаченных заказов
  CURRENT_ORDER: 'currentOrder',     // Текущий активный заказ
  CURRENT_ORDER_ID: 'currentOrderId', // Выбранный текущий id
  USER_ORDERS: 'userOrders',         // Массив всех заказов пользователя (история)
} as const

/**
 * Интерфейс для данных заказа в localStorage
 */
export interface StorageOrderData {
  orderId: string | number
  payment_url?: string | null
  external_payment_id?: string | null
  status: string
  payment_status: string
  created_at: string
  updated_at?: string
  total_amount?: number
  service_fee?: number
  serviceFeePercent?: number
  payment_expires_at?: string
  payment_url_expired?: boolean
}

/**
 * ❌ DEPRECATED: Больше не используется
 * Неоплаченные заказы управляются через polling и Pinia Store
 * 
 * @deprecated Используйте useUnpaidOrdersStore вместо этого
 */
export function saveUnpaidOrderToStorage(_orderData: StorageOrderData): void {
  consola.warn('⚠️ saveUnpaidOrderToStorage is deprecated. Use useUnpaidOrdersStore instead.')
  // Функция оставлена для обратной совместимости, но ничего не делает
}

/**
 * ❌ DEPRECATED: Больше не используется
 * @deprecated Миграция больше не нужна
 */
export function migrateSingleUnpaidToArray(): void {
  // Функция оставлена для обратной совместимости, но ничего не делает
}

/**
 * ❌ DEPRECATED: Больше не используется
 * Неоплаченные заказы управляются через polling и Pinia Store
 * 
 * @deprecated Используйте useUnpaidOrdersStore.summaries вместо этого
 */
export function getUnpaidOrdersFromStorage(): StorageOrderData[] {
  consola.warn('⚠️ getUnpaidOrdersFromStorage is deprecated. Use useUnpaidOrdersStore.summaries instead.')
  return []
}

/**
 * ❌ DEPRECATED: Больше не используется
 * @deprecated Используйте useUnpaidOrdersStore вместо этого
 */
export function saveUnpaidOrdersToStorage(_orders: StorageOrderData[]): void {
  consola.warn('⚠️ saveUnpaidOrdersToStorage is deprecated. Use useUnpaidOrdersStore instead.')
  // Функция оставлена для обратной совместимости, но ничего не делает
}

/**
 * ❌ DEPRECATED: Больше не используется
 * @deprecated Используйте useUnpaidOrdersStore.addSummary вместо этого
 */
export function addOrUpdateUnpaidOrder(_orderData: StorageOrderData): void {
  consola.warn('⚠️ addOrUpdateUnpaidOrder is deprecated. Use useUnpaidOrdersStore.addSummary instead.')
  // Функция оставлена для обратной совместимости, но ничего не делает
}

/**
 * ❌ DEPRECATED: Больше не используется
 * @deprecated Используйте useUnpaidOrdersStore.removeOrder вместо этого
 */
export function removeUnpaidOrderById(_orderId: string | number): void {
  consola.warn('⚠️ removeUnpaidOrderById is deprecated. Use useUnpaidOrdersStore.removeOrder instead.')
  // Функция оставлена для обратной совместимости, но ничего не делает
}

/**
 * ❌ DEPRECATED: Больше не используется
 * @deprecated Используйте useUnpaidOrdersStore.removeOrder вместо этого
 */
export function removeUnpaidOrderFromStorage(_orderId: string | number): void {
  consola.warn('⚠️ removeUnpaidOrderFromStorage is deprecated. Use useUnpaidOrdersStore.removeOrder instead.')
  // Функция оставлена для обратной совместимости, но ничего не делает
}

/**
 * Сохраняет заказ в массив всех заказов пользователя
 * @param order - заказ для сохранения
 */
export function saveOrderToUserHistory(order: Order): void {
  if (!process.client) return
  
  try {
    // Преобразуем время в московское перед сохранением
    const orderWithMoscowTime = {
      ...order,
      createdAt: order.createdAt ? convertUtcToMoscowTime(order.createdAt) : order.createdAt,
      updatedAt: order.updatedAt ? convertUtcToMoscowTime(order.updatedAt) : order.updatedAt,
      created_at: order.created_at ? convertUtcToMoscowTime(order.created_at) : order.created_at,
      updated_at: order.updated_at ? convertUtcToMoscowTime(order.updated_at) : order.updated_at,
    }
    
    consola.debug('🕐 [saveOrderToUserHistory] Time conversion:', {
      originalUTC: order.createdAt || order.created_at,
      moscowTime: orderWithMoscowTime.createdAt || orderWithMoscowTime.created_at,
    })
    
    const userOrders = JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_ORDERS) || '[]')
    
    // Проверяем, нет ли уже такого заказа
    const existingIndex = userOrders.findIndex((o: any) => o.id === order.id)
    
    if (existingIndex === -1) {
      // Добавляем новый заказ
      userOrders.push(orderWithMoscowTime)
      consola.debug('💾 New order added to user history:', order.id)
    } else {
      // Обновляем существующий заказ
      userOrders[existingIndex] = orderWithMoscowTime
      consola.debug('💾 Existing order updated in user history:', order.id)
    }
    
    // Сохраняем обновленный массив
    localStorage.setItem(STORAGE_KEYS.USER_ORDERS, JSON.stringify(userOrders))
    consola.debug('💾 User orders saved to localStorage, total:', userOrders.length)
  } catch (error) {
    consola.error('Failed to save order to user history:', error)
  }
}

/**
 * Получает все заказы пользователя из localStorage
 * @returns массив всех заказов пользователя
 */
export function getUserOrdersFromStorage(): Order[] {
  if (!process.client) return []
  
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_ORDERS)
    if (!stored) return []
    
    const userOrders = JSON.parse(stored)
    consola.debug('📋 Loaded user orders from localStorage:', userOrders.length)
    return userOrders
  } catch (error) {
    consola.error('Failed to load user orders from localStorage:', error)
    return []
  }
}

/**
 * Получает активные заказы (оплаченные, но не завершенные)
 * @returns массив активных заказов
 */
export function getActiveOrdersFromStorage(): Order[] {
  if (!process.client) return []
  
  try {
    const userOrders = getUserOrdersFromStorage()
    
    // Фильтруем активные заказы
    const activeOrders = userOrders.filter(order => 
      order.payment_status === 'paid' && 
      !['delivered', 'cancelled'].includes(order.status),
    )
    
    consola.debug('📋 Active orders from localStorage:', activeOrders.length)
    return activeOrders
  } catch (error) {
    consola.error('Failed to get active orders from localStorage:', error)
    return []
  }
}

/**
 * ❌ DEPRECATED: Больше не используется
 * Неоплаченные заказы управляются через polling и Pinia Store
 * 
 * @deprecated Используйте useUnpaidOrdersStore.selectedOrder вместо этого
 */
export function loadUnpaidOrderFromStorage(): StorageOrderData | null {
  consola.warn('⚠️ loadUnpaidOrderFromStorage is deprecated. Use useUnpaidOrdersStore.selectedOrder instead.')
  return null
}

/**
 * Очищает данные о неоплаченном заказе из localStorage
 * 
 * ⚠️ ЧАСТИЧНО DEPRECATED: Очистка localStorage для неоплаченных заказов
 * больше не требуется, но функция оставлена для очистки legacy данных
 */
export function clearUnpaidOrderFromStorage(): void {
  if (!process.client) return
  
  try {
    localStorage.removeItem(STORAGE_KEYS.UNPAID_ORDER)
    localStorage.removeItem(STORAGE_KEYS.UNPAID_ORDERS)
    consola.debug('🧹 Cleared unpaid order from localStorage (legacy cleanup)')
  } catch (error) {
    consola.error('Failed to clear unpaid order from localStorage:', error)
  }
}

/**
 * Очищает данные о завершенном заказе из localStorage
 */
export function clearCompletedOrderFromStorage(): void {
  if (!process.client) return
  
  try {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_ORDER)
    localStorage.removeItem(STORAGE_KEYS.UNPAID_ORDER)
    consola.debug('🧹 Cleared completed order from localStorage')
  } catch (error) {
    consola.error('Failed to clear completed order from localStorage:', error)
  }
}

/**
 * Очищает все данные заказов из localStorage
 */
export function clearAllOrdersFromStorage(): void {
  if (!process.client) return
  
  try {
    localStorage.removeItem(STORAGE_KEYS.UNPAID_ORDER)
    localStorage.removeItem(STORAGE_KEYS.CURRENT_ORDER)
    localStorage.removeItem(STORAGE_KEYS.USER_ORDERS)
    consola.debug('🧹 Cleared all orders from localStorage')
  } catch (error) {
    consola.error('Failed to clear all orders from localStorage:', error)
  }
}

/**
 * Очищает только историю заказов, оставляя текущий заказ
 */
export function clearUserOrdersHistory(): void {
  if (!process.client) return
  
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_ORDERS)
    consola.debug('🧹 Cleared user orders history from localStorage')
  } catch (error) {
    consola.error('Failed to clear user orders history from localStorage:', error)
  }
}

/**
 * Получает/устанавливает выбранный текущий orderId
 */
export function getCurrentOrderIdFromStorage(): string | number | null {
  if (!process.client) return null
  const raw = localStorage.getItem(STORAGE_KEYS.CURRENT_ORDER_ID)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

export function setCurrentOrderIdToStorage(orderId: string | number | null): void {
  if (!process.client) return
  if (orderId == null) {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_ORDER_ID)
    return
  }
  try {
    localStorage.setItem(STORAGE_KEYS.CURRENT_ORDER_ID, JSON.stringify(orderId))
  } catch {
    localStorage.setItem(STORAGE_KEYS.CURRENT_ORDER_ID, String(orderId))
  }
}

/**
 * Проверяет валидность данных заказа из localStorage
 * @param data - данные для проверки
 * @returns true если данные валидны
 */
export function isValidStorageOrderData(data: any): data is StorageOrderData {
  return data && 
         typeof data === 'object' &&
         (typeof data.orderId === 'string' || typeof data.orderId === 'number') &&
         typeof data.status === 'string' &&
         typeof data.payment_status === 'string' &&
         typeof data.created_at === 'string'
}

/**
 * Конвертирует данные из localStorage в объект заказа
 * @param storageData - данные из localStorage
 * @returns объект заказа
 */
export function convertStorageDataToOrder(storageData: StorageOrderData): Order {
  return {
    id: storageData.orderId,
    status: storageData.status as any,
    payment_status: storageData.payment_status as any,
    external_payment_id: storageData.external_payment_id || null,
    payment_url: storageData.payment_url || null,
    order_type: 'takeaway' as OrderType,
    payment_method: 'card' as PaymentMethod,
    total_amount: storageData.total_amount || 0,
    service_fee: storageData.service_fee || 0,
    serviceFeePercent: storageData.serviceFeePercent || 0,
    delivery_cost: 0,
    points_used: 0,
    points_earned: 0,
    delivery_address: null,
    notes: null,
    created_at: storageData.created_at,
    items: [], // Пустой массив, будет загружен при необходимости
    branch: undefined,
    payment_expires_at: storageData.payment_expires_at,
    payment_url_expired: storageData.payment_url_expired || false,
  }
}

/**
 * Конвертирует объект заказа в данные для localStorage
 * @param order - объект заказа
 * @returns данные для сохранения в localStorage
 */
export function convertOrderToStorageData(order: Order): StorageOrderData {
  // Преобразуем время в московское перед сохранением
  const moscowCreatedAt = order.created_at ? convertUtcToMoscowTime(order.created_at) : order.created_at
  const moscowUpdatedAt = order.updated_at ? convertUtcToMoscowTime(order.updated_at) : order.updated_at
  
  consola.debug('🕐 [convertOrderToStorageData] Time conversion:', {
    originalUTC: order.created_at,
    moscowTime: moscowCreatedAt,
  })
  
  return {
    orderId: order.id,
    payment_url: order.payment_url,
    external_payment_id: order.external_payment_id,
    status: order.status,
    payment_status: order.payment_status,
    created_at: moscowCreatedAt,
    updated_at: moscowUpdatedAt,
    total_amount: order.total_amount,
    service_fee: order.service_fee,
    serviceFeePercent: order.serviceFeePercent,
    payment_expires_at: order.payment_expires_at,
    payment_url_expired: order.payment_url_expired,
  }
}

/**
 * ❌ DEPRECATED: Больше не используется
 * Неоплаченные заказы управляются через polling и Pinia Store
 * 
 * @deprecated Используйте useUnpaidOrdersStore.selectedOrder вместо этого
 */
export function restoreOrderFromStorage(): Order | null {
  consola.warn('⚠️ restoreOrderFromStorage is deprecated. Use useUnpaidOrdersStore.selectedOrder instead.')
  return null
}

/**
 * ❌ DEPRECATED: Больше не используется
 * @deprecated Используйте useUnpaidOrdersStore.hasUnpaidOrders вместо этого
 */
export function hasUnpaidOrderInStorage(): boolean {
  consola.warn('⚠️ hasUnpaidOrderInStorage is deprecated. Use useUnpaidOrdersStore.hasUnpaidOrders instead.')
  return false
}

/**
 * Логирует операцию с localStorage
 * @param operation - описание операции
 * @param data - данные операции (опционально)
 */
export function logStorageOperation(operation: string, data?: any): void {
  consola.debug(`💾 Storage operation: ${operation}`, data || '')
}